import React, { useEffect, useMemo } from 'react';
import { Spinner } from '../controls/Spinner/Spinner';
import { isElectron } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { ILockerFixture, useLockerContext } from './lockers';
import {
    ILicenseScanResult,
    IQRCodeScanResult,
    IScanError,
    MZeroProvider,
    QRCodeData,
    ScanErrorCode,
    useMZeroExitToDesktop,
    useMZeroLocker,
    useMZeroQRScanner,
    useMZeroScanLicense,
    useMZeroState,
    useMZeroCardReader,
    PaymentError,
    ICardTransactionResult,
} from './mzero';
import { StormProvider, useStormLocker, useStormQRScanner, useStormScanLicense, useStormState } from './stormApi';
import { LockerStatus, StormApiReadLockerStatus } from '../helpers/stormExpress';
import { useKioskState } from './Kiosk/kiosk';
import { isDevelopment } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import { ActionMap, GoMotoFunction } from '../API/GoMoto.types';
interface HardwareProviderState {
    isLoading: boolean;
    isLinux: boolean;
    hostname: string;
}

enum Types {
    Loaded = 'LOADED'
}

type HardwareProviderPayload = {
    [Types.Loaded]: { linux: boolean; hostname: string; };
};

type HardwareProviderActions = ActionMap<HardwareProviderPayload>[keyof ActionMap<HardwareProviderPayload>];

const hardwareProviderStateReducer = (state: HardwareProviderState, action: HardwareProviderActions) => {
    switch (action.type) {
        case Types.Loaded:
            return { isLoading: false,
                isLinux: action.payload.linux,
                hostname: action.payload.hostname } as typeof state;
        default:
            return state;
    }
};

const initialHardwareProviderState: HardwareProviderState = { isLoading: isElectron(),
    isLinux: false,
    hostname: '' };
const HardwareStateContext = React.createContext<HardwareProviderState>(initialHardwareProviderState);
export function useHardwareProviderState() {
    return React.useContext(HardwareStateContext);
}

export const HardwareProvider: React.FC<{ children: React.ReactNode; }> = (props) => {
    const [state, reducer] = React.useReducer(hardwareProviderStateReducer, initialHardwareProviderState);

    useEffect(() => {
        let linux: boolean = false;
        let hostname: string = '';

        if (isElectron() && window.os) {
            const os = window.os;
            linux = os.platform() === 'linux';
            hostname = os.hostname();
        }
        reducer({ type: Types.Loaded,
            payload: { linux,
                hostname } });
    }, []);

    return (
        <HardwareStateContext.Provider value={state}>
            {state.isLoading ? <Spinner /> : isElectron() ? state.isLinux ? <StormProvider {...props} /> : <MZeroProvider {...props} /> : <>{props.children}</>}
        </HardwareStateContext.Provider>
    );
};

export const useIsHardwareConnected = () => {
    const { connected: mzeroConnected } = useMZeroState();
    const { connected: stormConnected } = useStormState();
    const { isLinux } = useHardwareProviderState();

    return () => (isLinux ? stormConnected : mzeroConnected);
};

export const useIsHardwareError = () => {
    const { error: mzeroError } = useMZeroState();
    // TO DO handle any storm errors

    return () => mzeroError;
};

export interface QRCodeScanner {
    result?: QRCodeData;
    rawResult?: string;
    clearResult: () => void;
    setResult?: (result: string) => void;
    error?: IScanError;
    start: () => void;
    stop: () => void;
    inProgress: boolean;
    reset: () => void;
    simulateError?: (errorCode: ScanErrorCode) => void;
    simulateResult?: (result: IQRCodeScanResult) => void;
}

export const useQRScanner: (saveResultAsCheckIn?: boolean) => QRCodeScanner = (saveResultAsCheckIn: boolean = true) => {
    const { isLinux } = useHardwareProviderState();
    const stormScanner = useStormQRScanner(saveResultAsCheckIn);
    const mzeroScanner = useMZeroQRScanner(saveResultAsCheckIn);

    return isLinux ? stormScanner : mzeroScanner;
};

interface LockerControl {
    loading: boolean;
    getNextAvailableLocker: (ignoreLockers?: number[]) => Promise<ILockerFixture | undefined>;
    storeInLocker: (lockerNumber: number, appointmentKey?: string) => Promise<void>;
    lockers: ILockerFixture[];
    retrieveFromLocker: (lockerNumber: number, appointmentKey?: string, onOpen?: GoMotoFunction<[void], void>) => Promise<ILockerFixture | false>;
    openLocker: (lockerNumber: number) => Promise<ILockerFixture | false>;
    exchangeKeyAtLocker: GoMotoFunction<[number, string?, GoMotoFunction<[void], void>?], Promise<ILockerFixture | false>>;
    findLockerById: (lockerNumber: number) => ILockerFixture | undefined;
    error?: string;
    lockersEnabled: boolean;
}

export const useLocker: (onLockerOpen?: GoMotoFunction<[void], void>) => LockerControl = (onLockerOpen) => {
    const { isLinux } = useHardwareProviderState();
    const { lockers } = useLockerContext();

    const stormLocker = useStormLocker(onLockerOpen);
    const mzeroLocker = useMZeroLocker(onLockerOpen);

    const { mzero } = useMZeroState();
    const { keyStorageType } = useKioskState();

    const getNextAvailableLocker = React.useCallback(
        async (ignoreLockers: number[] = []) => {
            /**
             * Randomly pick one of the available lockers
             */

            if (isDevelopment() || keyStorageType === 'keydrop') {
                const availableLockers = lockers?.filter((locker) => locker.Status === 'Available' && !ignoreLockers.includes(locker.Id));

                if (availableLockers?.length > 0) {
                    return availableLockers[Math.floor(Math.random() * availableLockers.length)];
                }
            }
            else if (!isLinux) {
                const availableLockers: ILockerFixture[] = await new Promise<ILockerFixture[]>((resolve) => {
                    mzero?.create.locker.checkLockers()
                        .start({
                            Done: (err, data) => {
                                if (err || !data.Lockers) {
                                    resolve([]);
                                }
                                else {
                                    resolve(
                                        data.Lockers.filter(
                                            (locker) => locker.Status === 'Available' && locker.DoorStatus !== 'Open' && !ignoreLockers.includes(locker.Id),
                                        ),
                                    );
                                }
                            },
                        });
                });

                if (availableLockers?.length > 0) {
                    return availableLockers[Math.floor(Math.random() * availableLockers.length)];
                }
            }
            else {
                const availableLockers = lockers?.filter((locker) => locker.Status === 'Available' && !ignoreLockers.includes(locker.Id));

                while (availableLockers?.length > 0) {
                    const num = Math.floor(Math.random() * availableLockers.length);

                    const status: LockerStatus = await StormApiReadLockerStatus(availableLockers[num].Id);

                    if (status !== LockerStatus.Open) {
                        return availableLockers[num];
                    }

                    availableLockers.splice(num, 1);
                }
            }

            return undefined;
        },
        [isLinux, keyStorageType, lockers, mzero],
    );
    const findLockerById = React.useCallback((id: ILockerFixture['Id']) => lockers?.find((locker) => locker.Id === id), [lockers]);

    return {
        ...(isLinux ? stormLocker : mzeroLocker),
        lockers,
        getNextAvailableLocker,
        findLockerById,
    };
};

interface ExitToDesktopControl {
    exit?: (reason?: string) => void;
}

export const useExitToDesktop: () => ExitToDesktopControl = () => {
    const { isLinux } = useHardwareProviderState();
    const mzeroExit = useMZeroExitToDesktop();

    return {
        exit: isLinux ? undefined : mzeroExit.exit,
    };
};

interface LicenseScanControl {
    error?: IScanError;
    result?: ILicenseScanResult;
    simulateResult: () => void;
    licenseScanEnabled: boolean;
    start: () => void;
    stop: () => void;
    inProgress: boolean;
    reset: () => void;
    simulateError?: (errorCode: ScanErrorCode) => void;
    /**
     * MZero does not provide a rawResult
     */
    rawResult: string | undefined;
}

export const useScanLicense: () => LicenseScanControl = () => {
    const { isLinux } = useHardwareProviderState();
    const mzeroScan = useMZeroScanLicense();
    const stormScan = useStormScanLicense();

    return isLinux ? stormScan : mzeroScan;
};

interface CardReader {
    cardReaderEnabled: Promise<boolean>;
    pay: (amount: number) => void;
    loading: boolean | undefined;
    abort: () => void;
    reset: () => void;
    error: PaymentError | undefined;
    results: ICardTransactionResult[];
}

export const useCardReader: () => CardReader = () => {
    const { isLinux } = useHardwareProviderState();
    const mzeroCardReader = useMZeroCardReader();
    const linuxCardReader = useMemo<CardReader>(
        () => ({
            cardReaderEnabled: Promise.resolve(false),
            pay: () => null,
            loading: false,
            abort: () => null,
            reset: () => null,
            error: undefined,
            results: [],
        }),
        [],
    );

    return isLinux ? linuxCardReader : mzeroCardReader;
};
